import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useUserStore } from 'store';
import { PL_API, downloadBlobResponse, purchaseErrorMessage } from '../purchase-invoices/purchaseUtils';

const PROVIDERS = ['netvisor', 'fennoa', 'procountor'];
const INTAKE_MODES = ['none', 'bank', 'accounting'];
const PUSH_MODES = ['approve', 'pay', 'manual'];
const RECEIPT_METHODS = ['card', 'cash', 'other'];

function CopyField({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      // clipboard unavailable; the text stays selectable
    }
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all' }} data-testid={testId}>
        {value}
      </Typography>
      <IconButton size="small" onClick={copy} color={copied ? 'success' : 'default'}>
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
}

function CategoryDialog({ t, open, onClose, category, providers, accounts, onSaved, notify }) {
  const [form, setForm] = useState({ name: '', code: '', vat_deductible: true, account_mappings: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: category?.name || '',
        code: category?.code || '',
        vat_deductible: category?.vat_deductible ?? true,
        account_mappings: { ...(category?.account_mappings || {}) },
      });
    }
  }, [open, category]);

  const slug = (value) =>
    value
      .toLowerCase()
      .replace(/[åä]/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);

  const save = async () => {
    setSaving(true);
    const payload = { ...form, code: form.code || slug(form.name) };
    try {
      const response = category
        ? await axios.patch(`${PL_API}/cost-categories/${category.id}/`, payload)
        : await axios.post(`${PL_API}/cost-categories/`, payload);
      onSaved(response.data);
      onClose();
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth data-testid="pl-category-dialog">
      <DialogTitle>{category ? t('purchaseLedger.settings.categories.edit') : t('purchaseLedger.settings.categories.add')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField autoFocus size="small" label={t('purchaseLedger.settings.categories.name')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} inputProps={{ 'data-testid': 'pl-category-name' }} />
        <TextField size="small" label={t('purchaseLedger.settings.categories.code')} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} helperText={t('purchaseLedger.settings.categories.codeHelp')} />
        <FormControlLabel control={<Checkbox checked={form.vat_deductible} onChange={(e) => setForm((f) => ({ ...f, vat_deductible: e.target.checked }))} />} label={t('purchaseLedger.settings.categories.vatDeductible')} />
        {PROVIDERS.map((provider) => {
          const list = accounts[provider];
          const value = form.account_mappings[provider] || '';
          const set = (v) => setForm((f) => ({ ...f, account_mappings: { ...f.account_mappings, [provider]: v } }));
          const label = t('purchaseLedger.settings.categories.accountFor', { provider: provider.charAt(0).toUpperCase() + provider.slice(1) });
          if (providers.includes(provider) && Array.isArray(list) && list.length > 0) {
            return (
              <FormControl key={provider} size="small">
                <InputLabel>{label}</InputLabel>
                <Select value={value} label={label} onChange={(e) => set(e.target.value)}>
                  <MenuItem value="">
                    <em>{t('purchaseLedger.settings.categories.noAccount')}</em>
                  </MenuItem>
                  {list.map((a) => {
                    const number = String(a.number ?? a.account_number ?? a.id ?? '');
                    return (
                      <MenuItem key={number} value={number}>
                        {number} {a.name || a.description || ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            );
          }
          return <TextField key={provider} size="small" label={label} value={value} onChange={(e) => set(e.target.value)} inputProps={{ 'data-testid': `pl-category-account-${provider}` }} />;
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('Cancel')}</Button>
        <Button variant="contained" onClick={save} disabled={saving || !form.name.trim()} data-testid="pl-category-save">
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// "Ostolaskut" sub-tab of the Ominaisuudet settings tab (eligible orgs only).
function FeaturesPurchaseLedgerTab({ t, onDirtyChange }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const [state, setState] = useState(null); // {activated, enabled, config}
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [intakeMode, setIntakeMode] = useState('none');
  const [requestMaventa, setRequestMaventa] = useState(false);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null });
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportFormat, setExportFormat] = useState('zip');
  const [exportCancelled, setExportCancelled] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);
  const provider = currentOrganization?.accounting_integration_provider || '';

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${PL_API}/config/`);
      setState(response.data);
      if (response.data?.config) {
        const c = response.data.config;
        setForm({
          payment_debtor_iban: c.payment_debtor_iban || '',
          payment_debtor_bic: c.payment_debtor_bic || '',
          payment_debtor_name: c.payment_debtor_name || '',
          default_due_days: c.default_due_days ?? 14,
          due_digest_enabled: !!c.due_digest_enabled,
          due_digest_recipients: (c.due_digest_recipients || []).join(', '),
          accounting_push_on: c.accounting_push_on || 'approve',
          receipt_default_payment_method: c.receipt_default_payment_method || 'card',
          intake_mode: c.intake_mode || 'none',
        });
        setIsDirty(false);
      }
      if (response.data?.activated) {
        const [statusResponse, categoriesResponse] = await Promise.all([
          axios.get(`${PL_API}/config/status/`),
          axios.get(`${PL_API}/cost-categories/`),
        ]);
        setStatus(statusResponse.data);
        setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
        if (provider) {
          axios
            .get(`${PL_API}/cost-categories/accounts/`, { params: { provider } })
            .then((r) => setAccounts((a) => ({ ...a, [provider]: r.data?.accounts || [] })))
            .catch(() => {});
        }
      }
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    }
  }, [notify, provider, t]);

  useEffect(() => {
    load();
  }, [load, currentOrganization?.organization_id]);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_due_days: parseInt(form.default_due_days, 10) || 14,
        due_digest_recipients: form.due_digest_recipients
          .split(/[,\s;]+/)
          .map((v) => v.trim())
          .filter(Boolean),
      };
      const response = await axios.patch(`${PL_API}/config/`, payload);
      setState(response.data);
      setIsDirty(false);
      notify(t('Organization updated successfully'));
      load();
    } catch (error) {
      notify(purchaseErrorMessage(t, error, 'Failed to update organization'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const activate = async () => {
    setBusy('activate');
    try {
      await axios.post(`${PL_API}/config/activate/`, { intake_mode: intakeMode, request_maventa_reception: requestMaventa });
      await fetchUser();
      await load();
      notify(t('purchaseLedger.settings.activated'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  const toggleEnabled = async (checked) => {
    setBusy('toggle');
    try {
      await axios.post(`${PL_API}/config/${checked ? 'activate' : 'deactivate'}/`, {});
      await fetchUser();
      await load();
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  const requestReception = async () => {
    setBusy('reception');
    try {
      await axios.post(`${PL_API}/config/request-maventa-reception/`, {});
      await load();
      notify(t('purchaseLedger.settings.receptionRequested'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  const pollNow = async () => {
    setBusy('poll');
    try {
      const response = await axios.post(`${PL_API}/config/poll-now/`, {});
      notify(t('purchaseLedger.settings.polled', { count: response.data?.created ?? 0 }));
    } catch (error) {
      if (error.response?.status === 429) notify(t('purchaseLedger.errors.cooldown'), 'warning');
      else notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  const deleteCategory = async (category) => {
    setBusy(`cat-${category.id}`);
    try {
      await axios.delete(`${PL_API}/cost-categories/${category.id}/`);
      setCategories((list) => list.filter((c) => c.id !== category.id).map((c) => c));
      load();
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  const runExport = async () => {
    setBusy('export');
    try {
      const response = await axios.get(`${PL_API}/invoices/export/`, {
        params: { from: exportFrom || undefined, to: exportTo || undefined, export_format: exportFormat, include_cancelled: exportCancelled ? '1' : '0' },
        responseType: 'blob',
      });
      downloadBlobResponse(response, `ostolaskut.${exportFormat}`);
    } catch (error) {
      notify(t('purchaseLedger.settings.exportFailed'), 'error');
    } finally {
      setBusy('');
    }
  };

  if (!state) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const setField = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setIsDirty(true);
  };

  if (!state.activated) {
    return (
      <Box data-testid="pl-settings-activate">
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{t('purchaseLedger.settings.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>{t('purchaseLedger.settings.intro')}</Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('purchaseLedger.settings.intakeQuestion')}</Typography>
        <RadioGroup value={intakeMode} onChange={(e) => setIntakeMode(e.target.value)}>
          {INTAKE_MODES.map((mode) => (
            <FormControlLabel key={mode} value={mode} control={<Radio />} label={t(`purchaseLedger.settings.intake.${mode}`)} data-testid={`pl-intake-${mode}`} />
          ))}
        </RadioGroup>
        {currentOrganization?.maventa_enabled && (
          <FormControlLabel control={<Checkbox checked={requestMaventa} onChange={(e) => setRequestMaventa(e.target.checked)} />} label={t('purchaseLedger.settings.requestMaventa')} sx={{ mt: 1 }} />
        )}
        <Alert severity="info" sx={{ my: 2, maxWidth: 640 }}>{t('purchaseLedger.settings.retentionNote')}</Alert>
        <Button variant="contained" onClick={activate} disabled={busy === 'activate'} startIcon={busy === 'activate' ? <CircularProgress size={16} /> : null} data-testid="pl-activate">
          {t('purchaseLedger.settings.activate')}
        </Button>
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    );
  }

  const config = state.config || {};
  const steps = status?.steps || [];
  const stepText = (step) => {
    switch (step.key) {
      case 'maventa_reception':
        return step.done ? step.address : t(`purchaseLedger.settings.reception.${step.status || 'none'}`);
      case 'email_address':
        return null;
      case 'debtor_account':
        return step.done ? `${step.iban} ${step.bic ? `/ ${step.bic}` : ''}` : t('purchaseLedger.settings.steps.debtorMissing');
      case 'approvers':
        return `${t('purchaseLedger.settings.steps.approveRoles')}: ${(step.approve_roles || []).map((r) => t(`permissions.roles.${r}`, { defaultValue: r })).join(', ') || '-'} · ${t('purchaseLedger.settings.steps.payRoles')}: ${(step.pay_roles || []).map((r) => t(`permissions.roles.${r}`, { defaultValue: r })).join(', ') || '-'}`;
      case 'accounting':
        return step.provider
          ? step.supports_purchase_invoices
            ? t('purchaseLedger.settings.steps.accountingPush', { provider: step.provider })
            : t('purchaseLedger.settings.steps.accountingExportOnly', { provider: step.provider })
          : t('purchaseLedger.settings.steps.accountingNone');
      default:
        return null;
    }
  };

  return (
    <Box data-testid="pl-settings">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <FormControlLabel
          control={<Switch checked={!!state.enabled} onChange={(e) => toggleEnabled(e.target.checked)} disabled={busy === 'toggle'} data-testid="pl-enabled-toggle" />}
          label={t('purchaseLedger.settings.enabledToggle')}
        />
        <Button size="small" startIcon={<RefreshIcon />} onClick={pollNow} disabled={!!busy || !currentOrganization?.maventa_enabled} data-testid="pl-poll-now">
          {t('purchaseLedger.settings.pollNow')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mb: 3, maxWidth: 640 }}>{t('purchaseLedger.settings.enabledHelp')}</Typography>

      {/* Checklist */}
      <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '.08em' }}>{t('purchaseLedger.settings.checklistTitle')}</Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3, maxWidth: 760 }}>
        <List dense>
          {steps.map((step) => (
            <ListItem key={step.key} alignItems="flex-start" data-testid={`pl-step-${step.key}`}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                {step.done ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={t(`purchaseLedger.settings.steps.${step.key}`)}
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <Box>
                    {step.key === 'email_address' && <CopyField value={step.address} testId="pl-inbox-address" />}
                    {step.key === 'maventa_reception' && step.done && <CopyField value={step.address} testId="pl-maventa-address" />}
                    {step.key !== 'email_address' && !(step.key === 'maventa_reception' && step.done) && (
                      <Typography variant="body2" color="text.secondary">{stepText(step)}</Typography>
                    )}
                    {step.key === 'maventa_reception' && step.error && <Typography variant="caption" color="error.main">{step.error}</Typography>}
                    {step.key === 'maventa_reception' && !step.done && step.maventa_enabled && (
                      <Button size="small" sx={{ mt: 0.5 }} onClick={requestReception} disabled={!!busy} data-testid="pl-request-reception">
                        {t('purchaseLedger.settings.requestReception')}
                      </Button>
                    )}
                    {step.key === 'maventa_reception' && !step.maventa_enabled && (
                      <Typography variant="caption" color="text.secondary">{t('purchaseLedger.settings.maventaNotActive')}</Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Settings form */}
      {form && (
        <>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '.08em' }}>{t('purchaseLedger.settings.formTitle')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, maxWidth: 760, mb: 2, mt: 1 }}>
            <TextField size="small" label={t('purchaseLedger.settings.debtorIban')} value={form.payment_debtor_iban} onChange={(e) => setField('payment_debtor_iban')(e.target.value)} inputProps={{ 'data-testid': 'pl-debtor-iban' }} />
            <TextField size="small" label={t('purchaseLedger.settings.debtorBic')} value={form.payment_debtor_bic} onChange={(e) => setField('payment_debtor_bic')(e.target.value)} />
            <TextField size="small" label={t('purchaseLedger.settings.debtorName')} value={form.payment_debtor_name} onChange={(e) => setField('payment_debtor_name')(e.target.value)} />
            <TextField size="small" type="number" label={t('purchaseLedger.settings.defaultDueDays')} value={form.default_due_days} onChange={(e) => setField('default_due_days')(e.target.value)} inputProps={{ min: 0, max: 180 }} />
            <FormControl size="small">
              <InputLabel>{t('purchaseLedger.settings.accountingPushOn')}</InputLabel>
              <Select value={form.accounting_push_on} label={t('purchaseLedger.settings.accountingPushOn')} onChange={(e) => setField('accounting_push_on')(e.target.value)}>
                {PUSH_MODES.map((m) => (
                  <MenuItem key={m} value={m}>{t(`purchaseLedger.settings.pushMode.${m}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>{t('purchaseLedger.settings.receiptMethod')}</InputLabel>
              <Select value={form.receipt_default_payment_method} label={t('purchaseLedger.settings.receiptMethod')} onChange={(e) => setField('receipt_default_payment_method')(e.target.value)}>
                {RECEIPT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>{t(`purchaseLedger.paymentMethod.${m}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>{t('purchaseLedger.settings.intakeMode')}</InputLabel>
              <Select value={form.intake_mode} label={t('purchaseLedger.settings.intakeMode')} onChange={(e) => setField('intake_mode')(e.target.value)}>
                {INTAKE_MODES.map((m) => (
                  <MenuItem key={m} value={m}>{t(`purchaseLedger.settings.intake.${m}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <FormControlLabel control={<Switch checked={form.due_digest_enabled} onChange={(e) => setField('due_digest_enabled')(e.target.checked)} />} label={t('purchaseLedger.settings.digestToggle')} />
              <TextField size="small" fullWidth label={t('purchaseLedger.settings.digestRecipients')} value={form.due_digest_recipients} onChange={(e) => setField('due_digest_recipients')(e.target.value)} helperText={t('purchaseLedger.settings.digestRecipientsHelp')} disabled={!form.due_digest_enabled} sx={{ mt: 1 }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, maxWidth: 760, mb: 3 }}>
            <Button onClick={() => load()} disabled={!isDirty || saving}>{t('Cancel')}</Button>
            <Button variant="contained" onClick={handleSave} disabled={!isDirty || saving} data-testid="pl-settings-save">
              {saving ? <CircularProgress size={20} /> : <span>{t('Save Changes')}</span>}
            </Button>
          </Box>
        </>
      )}

      {/* Cost categories */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 760, mb: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '.08em' }}>{t('purchaseLedger.settings.categories.title')}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setCategoryDialog({ open: true, category: null })} data-testid="pl-category-add">
          {t('purchaseLedger.settings.categories.add')}
        </Button>
      </Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3, maxWidth: 760, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('purchaseLedger.settings.categories.name')}</TableCell>
              <TableCell>{t('purchaseLedger.settings.categories.vatDeductibleShort')}</TableCell>
              {PROVIDERS.map((p) => (
                <TableCell key={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((c) => (
              <TableRow key={c.id} sx={{ opacity: c.is_active ? 1 : 0.5 }} data-testid={`pl-category-${c.code}`}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.code}</Typography>
                </TableCell>
                <TableCell>{c.vat_deductible ? <Chip size="small" label={t('Yes')} /> : <Chip size="small" variant="outlined" label={t('No')} />}</TableCell>
                {PROVIDERS.map((p) => (
                  <TableCell key={p} sx={{ fontFamily: 'inherit' }}>{c.account_mappings?.[p] || '-'}</TableCell>
                ))}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={t('Edit')}>
                    <IconButton size="small" onClick={() => setCategoryDialog({ open: true, category: c })} data-testid={`pl-category-edit-${c.code}`}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('Delete')}>
                    <IconButton size="small" disabled={!!busy} onClick={() => deleteCategory(c)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Export */}
      <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '.08em' }}>{t('purchaseLedger.settings.exportTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 640 }}>{t('purchaseLedger.settings.exportHelp')}</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', maxWidth: 760 }}>
        <TextField size="small" type="date" label={t('purchaseLedger.settings.exportFrom')} value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={t('purchaseLedger.settings.exportTo')} value={exportTo} onChange={(e) => setExportTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('purchaseLedger.settings.exportFormat')}</InputLabel>
          <Select value={exportFormat} label={t('purchaseLedger.settings.exportFormat')} onChange={(e) => setExportFormat(e.target.value)}>
            <MenuItem value="zip">ZIP</MenuItem>
            <MenuItem value="xlsx">Excel</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox checked={exportCancelled} onChange={(e) => setExportCancelled(e.target.checked)} />} label={t('purchaseLedger.settings.exportIncludeCancelled')} />
        <Button variant="outlined" startIcon={busy === 'export' ? <CircularProgress size={16} /> : <DownloadIcon />} disabled={!!busy} onClick={runExport} data-testid="pl-export">
          {t('purchaseLedger.settings.export')}
        </Button>
      </Box>

      <CategoryDialog
        t={t}
        open={categoryDialog.open}
        category={categoryDialog.category}
        providers={provider ? [provider] : []}
        accounts={accounts}
        onClose={() => setCategoryDialog({ open: false, category: null })}
        onSaved={() => load()}
        notify={notify}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(FeaturesPurchaseLedgerTab);
